import {envIsTest} from '../../config';
import {store} from '../../setup';
import {useSelector} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import {getViewer, getViewerLoading} from '../../state/viewer/selectors';
import {getStringValueFromQueryString} from '../../util/query-string';
import {ExperimentConfig} from './types';
import {
  getAnonymousObservationalUnitID,
  getExperimentTreatment,
  useExperiment,
} from './utils';

export enum ChartBucketingExp {
  BucketAllGroupsTogether = 0,
  BucketOneGroupAtATime = 1,
}

export enum WorkspaceCachingExp {
  Disabled = 0,
  Enabled = 1,
}

export const Experiments: Record<string, ExperimentConfig> = {
  // Use new chart bucketing algorithm
  chartBucketing: {
    experimentGraphqlId:
      'RXhwZXJpbWVudDo2MmU0NmQ3MC03MjI2LTRlMjctODU0OS05ZmQ2NWY2MWI2ZDQ=',
    controlBucketValue: ChartBucketingExp.BucketAllGroupsTogether,
    bucketValues: new Set([
      ChartBucketingExp.BucketAllGroupsTogether,
      ChartBucketingExp.BucketOneGroupAtATime,
    ]),
  },
  // Enable workspace data caching on user's disk
  workspaceCaching: {
    experimentGraphqlId: `RXhwZXJpbWVudDo1MGM0MjZkNC02MTA3LTQ0MDAtYjk2ZS05Mjc4NThkNmE5NGQ=`,
    controlBucketValue: WorkspaceCachingExp.Disabled,
    bucketValues: new Set([
      WorkspaceCachingExp.Disabled,
      WorkspaceCachingExp.Enabled,
    ]),
  },
};

const workspaceCachingQSVal = getStringValueFromQueryString(
  `__WB_WORKSPACE_CACHING`
);
const workspaceCachingEnabledOverride =
  workspaceCachingQSVal === `true`
    ? true
    : workspaceCachingQSVal === `false`
    ? false
    : null;

export function useWorkspaceCachingEnabled(): boolean {
  /* eslint-disable react-hooks/rules-of-hooks --
   * Technically this is a conditional hook call which is not kosher,
   * BUT the environment is guaranteed not to ever change, so it's fine as a quick fix.
   * TODO: Fix this properly.
   **/
  if (envIsTest) {
    return true;
  }

  const viewer = useViewer();
  const viewerIsLoading = useSelector(getViewerLoading);

  const observationalUnitID = viewerIsLoading
    ? ``
    : viewer?.id ?? getAnonymousObservationalUnitID();

  const {experimentData} = useExperiment(
    Experiments.workspaceCaching,
    observationalUnitID
  );

  if (workspaceCachingEnabledOverride != null) {
    return workspaceCachingEnabledOverride;
  }

  const isEnabled = experimentData?.treatment === 1;
  return isEnabled;

  /* eslint-enable react-hooks/rules-of-hooks */
}

export function getWorkspaceCachingEnabled(): boolean {
  if (envIsTest) {
    return true;
  }

  const viewer = getViewer(store.getState());
  const viewerIsLoading = getViewerLoading(store.getState());

  const observationalUnitID = viewerIsLoading
    ? ``
    : viewer?.id ?? getAnonymousObservationalUnitID();

  const treatment = getExperimentTreatment(
    Experiments.workspaceCaching,
    observationalUnitID
  );

  if (workspaceCachingEnabledOverride != null) {
    return workspaceCachingEnabledOverride;
  }

  const isEnabled = treatment === 1;
  return isEnabled;
}
